import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Eye, ExternalLink, Mail, Phone, Plus, Star, Ship, Check, Anchor } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { api, errMsg } from "@/lib/api";
import { UserAvatar } from "@/components/common";
import { ProjectStatusChip } from "@/components/canvas/Badges";
import { fmtDate } from "@/lib/format";
import { compactMoney, ago, STATUS_LABEL, TYPE_LABEL } from "@/lib/sk";

export function Section({ title, count, right, children, testId, className }) {
  return (
    <section className={className} data-testid={testId}>
      <div className="flex items-baseline justify-between gap-3 border-b border-black/[0.07] pb-2.5">
        <h2 className="text-[13.5px] font-semibold text-foreground/85">{title}{count != null ? <span className="ml-2 font-mono text-[11.5px] font-medium text-muted-foreground/70">{count}</span> : null}</h2>
        {right}
      </div>
      {children}
    </section>
  );
}

const DOT = { draft: "bg-black/20", configured: "bg-[hsl(160_45%_38%)]", calculated: "bg-[hsl(35_80%_48%)]", quoted: "bg-primary", sent: "bg-primary", negotiation: "bg-[hsl(268_35%_48%)]", won: "bg-[hsl(152_50%_36%)]", lost: "bg-[hsl(0_50%_46%)]", new: "bg-[hsl(var(--brand))]" };

function ProjectRow({ p }) {
  const sub = [p.vessel?.name ? `${p.vessel.name}${p.vessel.type ? ` – ${p.vessel.type}` : ""}` : null, p.valves_count ? `${p.valves_count} ventiler` : null, p.hpu_count ? `${p.hpu_count} HPU · ${p.cabinet_count} skap` : null].filter(Boolean).join(" · ");
  const to = p.status === "new" ? "/innboks" : `/p/${p.id}`;
  return (
    <li>
      <Link to={to} className="group -mx-3 flex items-center gap-3 rounded-[12px] px-3 py-3 transition-colors hover:bg-black/[0.035]" data-testid={`customer-project-${p.number}`}>
        <span className={cn("h-2 w-2 shrink-0 rounded-full", DOT[p.status] || "bg-black/20")} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-[14px] font-medium"><span className="truncate">{p.name}</span><span className="shrink-0 font-mono text-[11px] text-muted-foreground/70">#{p.number}</span></div>
          <div className="truncate text-[12.5px] text-muted-foreground">{sub || STATUS_LABEL[p.status]}{p.clarification_pending ? " · venter på avklaring fra kunden" : ""}</div>
        </div>
        <ProjectStatusChip status={p.status} className="hidden sm:inline-flex" />
        <span className="hidden w-24 text-right font-mono text-[12.5px] tabular-nums md:inline">{p.value ? compactMoney(p.value) : ""}</span>
        {p.owner?.name ? <UserAvatar name={p.owner.name} initials={p.owner.initials} color={p.owner.color} src={p.owner.avatar_url} size="xs" className="hidden sm:flex" /> : null}
        <span className="hidden w-16 text-right text-[11.5px] text-muted-foreground lg:inline">{ago(p.updated_at)}</span>
        <ArrowRight className="h-3.5 w-3.5 -translate-x-1 text-muted-foreground opacity-0 transition-[transform,opacity] group-hover:translate-x-0 group-hover:opacity-100" />
      </Link>
    </li>
  );
}

export function Projects({ projects }) {
  const groups = [
    ["Nye forespørsler", projects.filter((p) => p.status === "new")],
    ["Pågår", projects.filter((p) => !["won", "lost", "new"].includes(p.status))],
    ["Avsluttet", projects.filter((p) => ["won", "lost"].includes(p.status))],
  ].filter(([, l]) => l.length);
  return (
    <Section title="Prosjekter" count={projects.length} testId="customer-projects">
      {projects.length ? groups.map(([label, list]) => (
        <div key={label} className="mt-4 first:mt-3">
          <div className="text-[11.5px] font-medium text-muted-foreground/70">{label}</div>
          <ul className="mt-1 divide-y divide-black/[0.04]">{list.map((p) => <ProjectRow key={p.id} p={p} />)}</ul>
        </div>
      )) : <p className="py-8 text-center text-[13px] text-muted-foreground">Ingen prosjekter ennå – start nytt arbeid for denne kunden.</p>}
    </Section>
  );
}

export function Quotes({ projects }) {
  const rows = projects.filter((p) => p.quote).sort((a, b) => (b.quote.sent_at || b.updated_at || "").localeCompare(a.quote.sent_at || a.updated_at || ""));
  if (!rows.length) return null;
  const label = (q) => (q.status === "accepted" ? "Akseptert" : q.status === "sent" ? "Sendt" : "Utkast");
  return (
    <Section title="Tilbud" count={rows.length} testId="customer-quotes">
      <ul className="mt-1 divide-y divide-black/[0.04]">
        {rows.map((p) => {
          const q = p.quote;
          return (
            <li key={q.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 py-3" data-testid={`customer-quote-${p.number}`}>
              <div className="min-w-0 flex-1 basis-[220px]">
                <div className="flex items-center gap-2 text-[14px] font-medium"><span>Tilbud v{q.version}</span><span className={cn("v-badge", q.status === "accepted" ? "v-ok" : q.status === "sent" ? "v-price" : "v-assumed")}>{label(q)}</span></div>
                <Link to={`/p/${p.id}?tab=tilbud`} className="block truncate text-[12.5px] text-muted-foreground hover:text-foreground"><span className="font-mono text-[11px]">#{p.number}</span> {p.name}</Link>
              </div>
              <div className="font-mono text-[13px] font-semibold tabular-nums">{compactMoney(q.total)}</div>
              <div className="w-[108px] text-[12px] text-muted-foreground">{q.sent_at ? `Sendt ${fmtDate(q.sent_at)}` : `Oppdatert ${ago(p.updated_at)}`}</div>
              <div className="inline-flex w-[84px] items-center gap-1.5 text-[12px] text-muted-foreground" title="Antall ganger kunden har åpnet tilbudet"><Eye className="h-3.5 w-3.5" /> {q.views || 0} {q.views === 1 ? "visning" : "visninger"}</div>
              <div className="w-[120px] text-[12px] text-muted-foreground">{q.valid_until ? `Gyldig til ${fmtDate(q.valid_until)}` : ""}</div>
              {q.token && q.status !== "draft" ? <a href={`/q/${q.token}`} target="_blank" rel="noreferrer" className="inline-flex h-8 items-center gap-1.5 rounded-full px-2.5 text-[12px] font-medium text-muted-foreground hover:bg-black/[0.05] hover:text-foreground" data-testid={`customer-quote-public-${p.number}`}>Slik kunden ser det <ExternalLink className="h-3 w-3" /></a> : null}
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

function ContactForm({ accountId, onDone }) {
  const [f, setF] = useState({ name: "", role: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return;
    setBusy(true);
    try { await api.post(`/accounts/${accountId}/contacts`, f); toast.success("Kontakt lagt til"); onDone(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const inp = "h-9 w-full rounded-[9px] border border-black/[0.1] bg-white px-2.5 text-[13px] outline-none transition-[border-color,box-shadow] focus:border-primary/50 focus:ring-4 focus:ring-primary/10";
  return (
    <form onSubmit={submit} className="grid gap-2 rounded-[14px] border border-black/[0.06] bg-[hsl(210_20%_98.5%)] p-3 sm:grid-cols-2" data-testid="customer-contact-form">
      <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Navn" className={inp} required autoFocus data-testid="customer-contact-name-input" />
      <input value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} placeholder="Rolle (f.eks. innkjøpssjef)" className={inp} data-testid="customer-contact-role-input" />
      <input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="E-post" type="email" className={inp} data-testid="customer-contact-email-input" />
      <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Telefon" className={inp} data-testid="customer-contact-phone-input" />
      <div className="flex justify-end gap-2 sm:col-span-2">
        <button type="button" onClick={onDone} className="h-8 rounded-full px-3 text-[12.5px] text-muted-foreground hover:text-foreground" data-testid="customer-contact-cancel-button">Avbryt</button>
        <button type="submit" disabled={busy} className="h-8 rounded-full bg-[hsl(var(--brand))] px-3.5 text-[12.5px] font-medium text-white hover:bg-[hsl(var(--brand-strong))] disabled:opacity-40" data-testid="customer-contact-save-button">Lagre</button>
      </div>
    </form>
  );
}

export function Contacts({ accountId, contacts, onChanged }) {
  const [adding, setAdding] = useState(false);
  return (
    <Section title="Kontakter" count={contacts.length} testId="customer-contacts" right={<button type="button" onClick={() => setAdding((v) => !v)} className="inline-flex h-7 items-center gap-1 rounded-full px-2 text-[12px] font-medium text-muted-foreground hover:bg-black/[0.05] hover:text-foreground" data-testid="customer-add-contact-button"><Plus className="h-3.5 w-3.5" /> Legg til</button>}>
      <div className="mt-3 space-y-1">
        {adding ? <ContactForm accountId={accountId} onDone={() => { setAdding(false); onChanged(); }} /> : null}
        {contacts.map((c) => (
          <div key={c.id} className="-mx-2 flex items-start gap-3 rounded-[12px] px-2 py-2.5 transition-colors hover:bg-black/[0.03]" data-testid={`customer-contact-${c.id}`}>
            <UserAvatar name={c.name} size="md" className="h-9 w-9" />
            <div className="min-w-0 flex-1 text-[12.5px]">
              <div className="flex items-center gap-1.5 text-[13.5px] font-medium">{c.name}{c.is_decision_maker ? <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--brand-soft))] px-1.5 py-px text-[10.5px] font-semibold text-[hsl(208_90%_34%)]"><Star className="h-2.5 w-2.5 fill-current" /> Beslutningstaker</span> : null}</div>
              {c.role ? <div className="text-muted-foreground">{c.role}</div> : null}
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                {c.email ? <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground" data-testid={`customer-contact-email-${c.id}`}><Mail className="h-3 w-3" />{c.email}</a> : null}
                {c.phone ? <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground"><Phone className="h-3 w-3" />{c.phone}</a> : null}
              </div>
            </div>
          </div>
        ))}
        {!contacts.length && !adding ? <p className="py-4 text-center text-[12.5px] text-muted-foreground">Ingen kontakter registrert.</p> : null}
      </div>
    </Section>
  );
}

export function Fleet({ vessels }) {
  if (!vessels.length) return null;
  return (
    <Section title="Fartøy" count={vessels.length} testId="customer-fleet">
      <ul className="mt-3 space-y-1">
        {vessels.map((v) => (
          <li key={v.name} className="-mx-2 flex items-start gap-3 rounded-[12px] px-2 py-2.5" data-testid={`customer-vessel-${v.name}`}>
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[hsl(var(--brand)/0.06)] text-[hsl(var(--brand-strong))]"><Ship className="h-4 w-4" strokeWidth={1.8} /></span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-[13.5px] font-medium"><span className="truncate">{v.name}</span>{v.hull_no && v.hull_no !== v.name ? <span className="font-mono text-[11px] text-muted-foreground">{v.hull_no}</span> : null}</div>
              <div className="truncate text-[12.5px] text-muted-foreground">{[v.type, v.class_society, v.valves ? `${v.valves} ventiler` : null].filter(Boolean).join(" · ")}</div>
              <div className="mt-1 flex flex-wrap gap-1">{v.projects.map((p) => <Link key={p.id} to={`/p/${p.id}`} className="inline-flex h-6 items-center gap-1.5 rounded-full border border-black/[0.08] bg-white px-2 font-mono text-[11px] text-foreground/75 hover:border-black/[0.2] hover:text-foreground"><span className={cn("h-1.5 w-1.5 rounded-full", DOT[p.status] || "bg-black/20")} />#{p.number}</Link>)}</div>
            </div>
          </li>
        ))}
      </ul>
    </Section>
  );
}

export function Systems({ systems, types, total }) {
  if (!systems.length) return null;
  const max = Math.max(1, ...systems.map((s) => s.count));
  return (
    <Section title="Ventiler på tvers av prosjektene" count={total} testId="customer-systems">
      <ul className="mt-3 space-y-2">
        {systems.map((s) => (
          <li key={s.name} className="flex items-center gap-3 text-[12.5px]"><span className="w-[92px] shrink-0 truncate text-muted-foreground">{s.name}</span><span className="h-1.5 flex-1 overflow-hidden rounded-full bg-black/[0.05]"><span className="chart-reveal block h-full rounded-full bg-[hsl(var(--brand))]" style={{ width: `${(s.count / max) * 100}%` }} /></span><span className="w-8 text-right font-mono tabular-nums">{s.count}</span></li>
        ))}
      </ul>
      {types?.length ? <div className="mt-3 flex flex-wrap gap-1.5">{types.map((t) => <span key={t.name} className="inline-flex h-6 items-center gap-1.5 rounded-full bg-black/[0.045] px-2 text-[11.5px] font-medium text-foreground/75">{TYPE_LABEL[t.name] || t.name} <span className="font-mono text-muted-foreground">{t.count}</span></span>)}</div> : null}
    </Section>
  );
}

export function Notes({ accountId, value }) {
  const [text, setText] = useState(value || "");
  const [state, setState] = useState("idle");
  const ref = useRef(null);
  useEffect(() => { const ta = ref.current; if (!ta) return; ta.style.height = "0px"; ta.style.height = `${Math.max(ta.scrollHeight, 72)}px`; }, [text]);
  const save = async () => {
    if ((value || "") === text) return;
    setState("saving");
    try { await api.patch(`/accounts/${accountId}`, { notes: text }); setState("saved"); setTimeout(() => setState("idle"), 1600); } catch (e) { toast.error(errMsg(e)); setState("idle"); }
  };
  return (
    <Section title="Interne notater" testId="customer-notes" right={state !== "idle" ? <span className="inline-flex items-center gap-1 text-[11.5px] text-muted-foreground" data-testid="customer-notes-state">{state === "saved" ? <Check className="h-3 w-3 text-primary" strokeWidth={3} /> : <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />}{state === "saved" ? "Lagret" : "Lagrer…"}</span> : null}>
      <textarea ref={ref} value={text} onChange={(e) => setText(e.target.value)} onBlur={save} placeholder="Rammeavtaler, preferanser, hvem som bestemmer, hva de ble misfornøyde med sist …" className="caret-brand mt-3 w-full resize-none rounded-[12px] border border-transparent bg-transparent px-2 py-1.5 text-[13.5px] leading-relaxed outline-none transition-[border-color,background-color] placeholder:text-muted-foreground/50 hover:bg-black/[0.025] focus:border-black/[0.1] focus:bg-white" data-testid="customer-notes-input" />
    </Section>
  );
}

export function Facts({ account, stats }) {
  const rows = [
    ["Kunde siden", stats.customer_since ? fmtDate(stats.customer_since, { year: true }) : "–"],
    ["Valuta", account.currency || "NOK"],
    ["Selger", account.legal_entity || "LOS Marine AS"],
    ["Siste aktivitet", stats.last_touch ? ago(stats.last_touch) : "–"],
  ];
  return (
    <Section title="Om kunden" testId="customer-facts">
      <dl className="mt-1 divide-y divide-black/[0.04] text-[13px]">{rows.map(([k, v]) => <div key={k} className="flex justify-between gap-3 py-2"><dt className="text-muted-foreground">{k}</dt><dd className="text-right font-medium">{v}</dd></div>)}</dl>
      {account.vessel_owner ? <div className="mt-2 inline-flex items-center gap-1.5 text-[12.5px] text-muted-foreground"><Anchor className="h-3.5 w-3.5" /> {account.vessel_owner}</div> : null}
    </Section>
  );
}
